import   React                   ,
       { useEffect               ,
	     useMemo                 ,
	     useState                }  from "react";

import   classNames                 from 'classnames';

import   styles                     from './UserListV2.module.scss';

import   Field                      from '../../widget/inputs/Field';
import   Select                     from '../../widget/inputs/Select';
import   AnimatedWaitingIcon2       from '../../widget/animations/animatedWaitingIcon2';
import   ColumnTitle                from '../../widget/dataDisplay/labels/ColumnTitle'
import   Window                     from '../../widget/windows/window';
import   UserRow                    from './UserRow';
import   UserSheetV2                from './UserSheetV2';

import { useTypedSelector           }  from '../../../store/useTypeSelector';
import { RootState                  }  from '../../../store/generalReducer';

import { User                    ,
         USER_STATUS_VALUES      ,
         USER_STATUS_LABELS      ,
         USER_COUNTRY_VALUES     }  from '../../../dataTypes/userManagment/types';
import { getUserList             ,
         updateUserLockStatus    ,
         deleteUser               }  from '../../../backendServices/userManagment/api';

export const UserListV2 = () => {

  const [ userSet2            , setUserSet2            ] = useState<User[]>([]);

  const [ showWaitingIcon     , setShowWaitingIcon     ] = useState(false);

  const [ filter               , setFilter               ] = useState('');
  const [ statusFilter         , setStatusFilter         ] = useState('');
  const [ lockedFilter         , setLockedFilter         ] = useState('');
  const [ countryFilter        , setCountryFilter        ] = useState('');
  const [ sortOption           , setSortOption           ] = useState('');

  const [ viewUserIndex       , setViewUserIndex       ] = useState<number | null>(null);
  const [ createNewUser       , setCreateNewUser       ] = useState<boolean>(false);

  const [ showMessageWindow    , setShowMessageWindow    ] = useState(false);
  const [ message              , setMessage              ] = useState(<></>);

  const [ showConfirmation     , setShowConfirmation     ] = useState(false);
  const [ userToToggleLock     , setUserToToggleLock     ] = useState<User | null>(null);

  const [ showDeleteConfirmation , setShowDeleteConfirmation ] = useState(false);
  const [ userToDelete           , setUserToDelete           ] = useState<User | null>(null);

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  useEffect(() => {

    const fetchData = async () => {

      try
       {
        setShowWaitingIcon(true);

        const usersResult = await getUserList();

        if (usersResult.payload)
         {
          setUserSet2 (usersResult.payload);
         }
       }
      catch (error)
       {
        console.error('Error fetching data:', error);
       }
      finally
       {
        setShowWaitingIcon(false);
       }
    };

    fetchData();
  }, []);

  const reinit = async () => {

    setShowWaitingIcon(true);

    const usersResult = await getUserList();

    if (usersResult.payload)
     {
      setUserSet2 (usersResult.payload);
     }

    setShowWaitingIcon(false);
  }

  const checkFilter = (subChaine: string, chaine: string) => {
    if (subChaine === '') return true;
    return chaine.toUpperCase().includes(subChaine.toUpperCase());
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const handleLockedFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLockedFilter(e.target.value);
  };

  const handleCountryFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryFilter(e.target.value);
  };

  const handleSortOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value);
  };

  // Liste affichée : dataset brut (userSet2) + recherche texte + filtres + tri
  const userSet = useMemo(() => {

    const filtered = userSet2.filter((user) =>
      (
       filter === ''                          ||
       checkFilter(filter, user.lastname)     ||
       checkFilter(filter, user.firstname)    ||
       checkFilter(filter, user.email)        ||
       checkFilter(filter, user.city)
      )                                                                  &&
      (statusFilter  === '' || user.status === statusFilter)            &&
      (lockedFilter  === '' || (lockedFilter === 'yes' ? user.locked : !user.locked)) &&
      // Champ historiquement vide pour les comptes créés avant l'ajout du pays — même défaut que UserRow/UserSheet(V2)
      (countryFilter === '' || (user.country === '' ? 'France' : user.country) === countryFilter)
    );

    if (sortOption === '') return filtered;

    const [ sortField , sortDirection ] = sortOption.split('_');

    const sorted = [ ...filtered ].sort((a, b) => {

      let comparison = 0;

      if (sortField === 'lastname')     comparison = a.lastname.localeCompare(b.lastname, 'fr');
      if (sortField === 'email')        comparison = a.email.localeCompare(b.email, 'fr');
      if (sortField === 'lastLogin')    comparison = (a.lastLoginDate?.getTime() ?? 0) - (b.lastLoginDate?.getTime() ?? 0);
      if (sortField === 'creationDate') comparison = (a.creationDate?.getTime() ?? 0)  - (b.creationDate?.getTime() ?? 0);

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return sorted;

  }, [ userSet2 , filter , statusFilter , lockedFilter , countryFilter , sortOption ]);

  const handleEditionClick = (index: number) => {
    setViewUserIndex(index);
  };

  const handleToggleLockClick = (user: User) => {
    setUserToToggleLock(user);
    setShowConfirmation(true);
  };

  const handleLockConfirmation = async (confirmed: boolean) => {

    if ( confirmed && userToToggleLock )
     {
      try
       {
        const response = await updateUserLockStatus ( userToToggleLock.id , !userToToggleLock.locked );

        if (response.code === 1)
              {
               setUserSet2 ((prev) => prev.map((user) => user.id === userToToggleLock.id ? { ...user, locked: !userToToggleLock.locked } : user));
              }
         else {
               setMessage (<>Impossible de modifier le verrouillage de ce compte</>);
               setShowMessageWindow(true);
              }
       }
      catch (error)
       {
        console.error(`Erreur lors du verrouillage du user ID ${userToToggleLock.id}:`, error);
        setMessage(<>Impossible de modifier le verrouillage de ce compte</>);
        setShowMessageWindow(true);
       }
     }

    setShowConfirmation(false);
    setUserToToggleLock(null);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmation = async (confirmed: boolean) => {

    if ( confirmed && userToDelete )
     {
      try
       {
        const response = await deleteUser ( userToDelete.id , authentication.admin?.id );

        if (response.code === 1)
              {
               setUserSet2 ((prev) => prev.filter((user) => user.id !== userToDelete.id));
              }
         else {
               setMessage (<>Impossible de supprimer ce compte</>);
               setShowMessageWindow(true);
              }
       }
      catch (error)
       {
        console.error(`Erreur lors de la suppression du user ID ${userToDelete.id}:`, error);
        setMessage(<>Impossible de supprimer ce compte</>);
        setShowMessageWindow(true);
       }
     }

    setShowDeleteConfirmation(false);
    setUserToDelete(null);
  };

  const handleMessageWindowClose = () => {
    setShowMessageWindow(false);
  };

  const handleNewUserCreationClick = () => {
    setCreateNewUser (true)
  };

  const handleUserCreate = ( id: number ) => {

    setCreateNewUser (false)
    reinit ()
  }

  const handleUserSheetClose = ( id: number ) => {

    setViewUserIndex (null)
    reinit ()
  }

  const renderComponent = () : JSX.Element => {

           if ( createNewUser )
                 {
                  return <UserSheetV2 onCreate = {handleUserCreate} onEdit = {handleUserCreate} />
                 }
            else {
                  if ( viewUserIndex !== null )
                        {
                         return <UserSheetV2 user   = {userSet[viewUserIndex]}
                                             onEdit = {handleUserSheetClose} />
                        }
                   else {
           return (
                    <div className={classNames(styles.frame)}>

                      <div className={classNames(styles.head)}>
                        <div className={classNames(styles.headTitleBlock)}>
                          <span className={classNames(styles.headTitle)}>Liste des utilisateurs</span>
                          <span className={classNames(styles.headSubtitle)}>
                            Consultez et gérez tous les comptes utilisateurs
                          </span>
                        </div>
                        <div className={classNames(styles.headActions)}>
                          <button className={classNames(styles.btn, styles.btnPrimary)}
                                  onClick={handleNewUserCreationClick}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <line x1="12" y1="5" x2="12" y2="19"/>
                              <line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            Créer un compte
                          </button>
                        </div>
                      </div>

                      <div className={classNames(styles.upper_bar)}>

                        <Field type        = "text"
                               placeholder = "Rechercher..."
                               value       = {filter}
                               width       = "280px"
                               onChange    = {handleFilterChange} />

                        <Select choices          = {[ '' , ...USER_STATUS_VALUES ]}
                                choicesLabel     = {[ 'Tous les statuts' , ...USER_STATUS_LABELS ]}
                                value            = {statusFilter}
                                showPlaceholder  = {false}
                                width            = "170px"
                                onChange         = {handleStatusFilterChange} />

                        <Select choices          = {[ '' , 'yes' , 'no' ]}
                                choicesLabel     = {[ 'Tous (verrouillé)' , 'Verrouillé' , 'Non verrouillé' ]}
                                value            = {lockedFilter}
                                showPlaceholder  = {false}
                                width            = "170px"
                                onChange         = {handleLockedFilterChange} />

                        <Select choices          = {[ '' , ...USER_COUNTRY_VALUES ]}
                                choicesLabel     = {[ 'Tous les pays' , ...USER_COUNTRY_VALUES ]}
                                value            = {countryFilter}
                                showPlaceholder  = {false}
                                width            = "170px"
                                onChange         = {handleCountryFilterChange} />

                        <Select choices          = {[ ''
                                                     , 'lastname_asc'     , 'lastname_desc'
                                                     , 'email_asc'        , 'email_desc'
                                                     , 'creationDate_desc', 'creationDate_asc'
                                                     , 'lastLogin_desc'   , 'lastLogin_asc'    ]}
                                choicesLabel     = {[ 'Trier par…'
                                                     , 'Nom (A → Z)'                     , 'Nom (Z → A)'
                                                     , 'Email (A → Z)'                   , 'Email (Z → A)'
                                                     , 'Date de création (récent)'       , 'Date de création (ancien)'
                                                     , 'Dernière connexion (récent)'     , 'Dernière connexion (ancien)' ]}
                                value            = {sortOption}
                                showPlaceholder  = {false}
                                width            = "230px"
                                onChange         = {handleSortOptionChange} />

                      </div>

                      <div className={classNames(styles.list_container)}>

                        <div className={classNames(styles.list)}>
                          <ColumnTitle titles = {[ 'Utilisateur' , 'Statut' , 'Verrouillé' , 'Localisation' , 'Date de création' , 'Dernière connexion' , 'Action' ]}
                                       widths = {[ '340px'       , '140px'  , '120px'      , 'auto'         , '190px'            , '190px'              , '175px'  ]} />

                          {
                           userSet.map ( (user, index) => (
                                                            <UserRow key          = {user.id}
                                                                     user         = {user}
                                                                     index        = {index}
                                                                     onEdit       = {handleEditionClick}
                                                                     onToggleLock = {handleToggleLockClick}
                                                                     onDelete     = {handleDeleteClick} />
                                                          )
                                        )
                          }
                        </div>

                      </div>

                    </div>
                  )
                        }
                 }
  }

  return (
    <div className={classNames(styles.root)}>

      {
        showWaitingIcon && <AnimatedWaitingIcon2 />
      }

      {
        showMessageWindow &&
        <Window message                 = {message}
                messageType             = "information"
                onClickOnValidateButton = {handleMessageWindowClose}
                validButtonText         = "Ok" />
      }

      {
        showConfirmation &&
        <Window
          message                 = {<>{`Êtes-vous sûr de vouloir ${userToToggleLock?.locked ? 'déverrouiller' : 'verrouiller'} le compte de ${userToToggleLock?.firstname} ${userToToggleLock?.lastname} ?`}</>}
          messageType             = "question"
          onClickOnValidateButton = {() => handleLockConfirmation(true)}
          onClickOnCancelButton   = {() => handleLockConfirmation(false)}
          validButtonText         = "Oui"
          cancelButtonText        = "Non" />
      }

      {
        showDeleteConfirmation &&
        <Window
          message                 = {<>{`Êtes-vous sûr de vouloir supprimer le compte de ${userToDelete?.firstname} ${userToDelete?.lastname} ? Le compte sera clôturé mais conservé.`}</>}
          messageType             = "question"
          onClickOnValidateButton = {() => handleDeleteConfirmation(true)}
          onClickOnCancelButton   = {() => handleDeleteConfirmation(false)}
          validButtonText         = "Oui"
          cancelButtonText        = "Non" />
      }

      {renderComponent ()}
    </div>
  );
};

export default UserListV2;
